import { useCallback } from 'react';

import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { useEnabledKnowledgeSources } from '@atlassian/conversation-assistant-store/controllers/connectors-store';
import { useEnsureConnectorsLoaded } from '@atlassian/conversation-assistant-store/controllers/connectors-store/use-ensure-connectors-loaded';

import { type RovoApp } from '../../../../controllers/chat-mode-store/types';
import { useChatModeStoreActions } from '../../../../controllers/chat-mode-store/useChatModeStoreActions';

import { getKnowledgeSourceDisplayName } from './utils/get-knowledge-source-display-name';

const normalizeSource = (source: string): string => source.toLowerCase().trim();

/**
 * When a prompt card has specific appIds, call this with those ids
 * after the user clicks the card so the Filter by Apps select shows only those apps.
 */
export const useSetAppFiltersByPromptAppIds: (props: AssistanceServiceInputParam) => {
	setAppFiltersByPromptAppIds: (chatModeKey: string, appIds: string[]) => void;
} = ({ assistanceServiceParams }) => {
	useEnsureConnectorsLoaded({ assistanceServiceParams });
	const { setAppFiltersFromPrompt } = useChatModeStoreActions();
	const [storeAllSources] = useEnabledKnowledgeSources();

	const setAppFiltersByPromptAppIds = useCallback(
		(chatModeKey: string, appIds: string[]) => {
			if (appIds.length === 0 || storeAllSources.length === 0) {
				return;
			}
			const normalizedIds = new Set(appIds.map(normalizeSource));
			// Don't clear all filters if the user hasn't connected to any of the apps
			const filteredApps: RovoApp[] = storeAllSources
				.filter((s) => normalizedIds.has(normalizeSource(s.source)) && s.isUserOAuthed)
				.map((s) => ({
					source: s.source,
					friendlyName: getKnowledgeSourceDisplayName(s.source, s.friendlyName),
					iconUrl: s.iconUrl,
				}));
			if (filteredApps.length > 0) {
				setAppFiltersFromPrompt(chatModeKey, filteredApps);
			}
		},
		[setAppFiltersFromPrompt, storeAllSources],
	);

	return { setAppFiltersByPromptAppIds };
};
